import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Plus, Edit3, Trash2 } from 'lucide-react';
import { backofficeApi, extractErrorMessage } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { MessageTemplateDto, MessageTemplateRequestDto } from '@kardume/types';
import { Badge, Button, ActionButton, Modal, TextInput, Switch, Table, TableContainer } from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import DataState from '../DataState';

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const BodyPreview = styled.span`
  display: block;
  max-width: 460px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textSecondary};
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const TagChip = styled.button`
  border: 1px solid ${(p) => p.theme.border};
  background: ${(p) => p.theme.surfaceAlt};
  border-radius: ${(p) => p.theme.radiusPill};
  padding: 0.2rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${(p) => p.theme.primary};
  cursor: pointer;

  &:hover {
    border-color: ${(p) => p.theme.primary};
  }
`;

const Label = styled.span`
  display: block;
  margin: 1rem 0 0.4rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: ${(p) => p.theme.textMuted};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  resize: vertical;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surface};
  color: ${(p) => p.theme.textPrimary};
  font: inherit;
  font-size: 0.9rem;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${(p) => p.theme.primary};
  }
`;

const Preview = styled.div`
  margin-top: 0.5rem;
  padding: 0.8rem 0.95rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.primaryLight};
  color: ${(p) => p.theme.textPrimary};
  font-size: 0.88rem;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
`;

const Warn = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.warning};
`;

const FieldHint = styled.p`
  margin: 0.45rem 0 0;
  font-size: 0.78rem;
  line-height: 1.45;
  color: ${(p) => p.theme.textMuted};
`;

const ToggleField = styled.label`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.85rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  font-size: 0.85rem;
  font-weight: 600;
  color: ${(p) => p.theme.textSecondary};
  cursor: pointer;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const EMPTY_FORM: MessageTemplateRequestDto = { name: '', body: '', description: '', active: true };

interface TemplatesTabProps {
  canManage: boolean;
}

/**
 * Templates com tags `{{...}}`.
 *
 * O preview não é enfeite: é o que impede `Olá {{nome_logista}},` - a tag escrita
 * com o nome errado - de sair para duzentas pessoas. Ele é renderizado pelo backend,
 * pelas mesmas regras que valem no envio, para não haver duas verdades.
 */
const TemplatesTab: React.FC<TemplatesTabProps> = ({ canManage }) => {
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => backofficeApi.listMessageTemplates(), []);
  const { data: tags } = useAsync(() => backofficeApi.listMessageTags(), []);

  const [isOpen, setIsOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MessageTemplateRequestDto>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<MessageTemplateDto | null>(null);
  const [removing, setRemoving] = useState(false);
  const [rendered, setRendered] = useState('');

  const templates = data ?? [];
  const knownTags = useMemo(() => (tags ?? []).map((t) => t.tag), [tags]);

  // O preview vem do backend, com as mesmas regras do envio. Debounce para não
  // disparar uma request por tecla.
  useEffect(() => {
    if (!isOpen || !form.body.trim()) {
      setRendered('');
      return undefined;
    }
    const timer = setTimeout(() => {
      backofficeApi
        .previewMessageTemplate({ body: form.body })
        .then((result) => setRendered(result.rendered))
        .catch(() => setRendered(''));
    }, 350);
    return () => clearTimeout(timer);
  }, [form.body, isOpen]);

  const usedTags = useMemo(() => {
    const matches = form.body.match(/\{\{\s*([\p{L}\p{N}_]+)\s*\}\}/gu) ?? [];
    return matches.map((raw) => raw.replace(/[{}\s]/g, ''));
  }, [form.body]);

  const unknownTags = useMemo(
    () => usedTags.filter((tag) => knownTags.length > 0 && !knownTags.includes(tag)),
    [usedTags, knownTags]
  );

  const openCreate = (): void => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const openEdit = (template: MessageTemplateDto): void => {
    setEditingId(template.id);
    setForm({
      name: template.name,
      body: template.body,
      description: template.description ?? '',
      active: template.active,
    });
    setIsOpen(true);
  };

  const bodyRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Insere a tag onde o cursor está, e não no fim do texto.
   *
   * Anexar no fim obriga a pessoa a recortar e colar a tag para o meio da frase -
   * e é justamente no meio que ela quase sempre vai ("a *{{empresa}}* tem tudo a
   * ver"). O `selectionStart` sobrevive ao clique no chip, então a posição lida
   * aqui é a última em que ela realmente estava escrevendo.
   */
  const insertTag = (tag: string): void => {
    const snippet = `{{${tag}}}`;
    const field = bodyRef.current;
    const start = field?.selectionStart ?? form.body.length;
    const end = field?.selectionEnd ?? form.body.length;
    // Um trecho selecionado é substituído pela tag, como faria qualquer editor.
    setForm((prev) => ({ ...prev, body: prev.body.slice(0, start) + snippet + prev.body.slice(end) }));
    // O React só reescreve o `value` no próximo paint: mover o cursor agora seria
    // desfeito por essa reescrita. Depois do frame, o caret fica logo após a tag,
    // que é onde a pessoa continua digitando.
    requestAnimationFrame(() => {
      if (!field) return;
      const caret = start + snippet.length;
      field.focus();
      field.setSelectionRange(caret, caret);
    });
  };

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      if (editingId) {
        await backofficeApi.updateMessageTemplate(editingId, form);
        showNotification('Template atualizado. Campanhas já criadas não mudam.', 'success');
      } else {
        await backofficeApi.createMessageTemplate(form);
        showNotification('Template criado.', 'success');
      }
      setIsOpen(false);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível salvar o template.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteMessageTemplate(deleting.id);
      showNotification('Template removido.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover o template.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      {canManage && (
        <Toolbar>
          <Button variant="primary" onClick={openCreate}>
            <Plus size={16} /> Novo template
          </Button>
        </Toolbar>
      )}

      <DataState
        loading={loading}
        error={error}
        isEmpty={templates.length === 0}
        emptyTitle="Nenhum template ainda"
        emptyDescription="Escreva a mensagem uma vez, com tags, e reaproveite em qualquer campanha."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Template</th>
                <th>Mensagem</th>
                <th style={{ whiteSpace: 'nowrap' }}>Tags</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Situação</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td>
                    <strong>{template.name}</strong>
                    {template.description && <FieldHint>{template.description}</FieldHint>}
                  </td>
                  <td>
                    <BodyPreview>{template.body}</BodyPreview>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{template.tags.length}</td>
                  <td style={{ textAlign: 'right' }}>
                    <Badge tone={template.active ? 'success' : 'secondary'}>
                      {template.active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </td>
                  <td>
                    <RowActions>
                      {canManage && (
                        <>
                          <ActionButton title="Editar" aria-label="Editar template" onClick={() => openEdit(template)}>
                            <Edit3 size={16} />
                          </ActionButton>
                          <ActionButton
                            title="Remover"
                            aria-label="Remover template"
                            color="#DC2626"
                            onClick={() => setDeleting(template)}
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        </>
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editingId ? 'Editar template' : 'Novo template'}
        width="640px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting}>
              Salvar
            </Button>
          </>
        }
      >
        <TextInput
          label="Nome"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          placeholder="Ex.: Primeiro contato - lojista"
        />
        <TextInput
          label="Descrição (opcional)"
          value={form.description ?? ''}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Para que serve este texto"
          containerStyle={{ marginTop: '0.75rem' }}
        />

        <Label>Mensagem</Label>
        <TextArea
          ref={bodyRef}
          value={form.body}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            setForm((prev) => ({ ...prev, body: e.target.value }))
          }
          placeholder="Olá {{primeiro_nome}}, tudo bem? Sou do Kardume Urbano e queria falar sobre a {{empresa}}..."
        />

        <Label>Tags disponíveis (clique para inserir)</Label>
        <TagRow>
          {(tags ?? []).map((tag) => (
            <TagChip
              key={tag.tag}
              type="button"
              title={tag.description}
              // Impede o chip de roubar o foco do textarea: sem isto o cursor some
              // da caixa a cada tag inserida e a pessoa tem de clicar de volta.
              onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
              onClick={() => insertTag(tag.tag)}
            >
              {`{{${tag.tag}}}`}
            </TagChip>
          ))}
        </TagRow>
        <FieldHint>
          Colunas extras das planilhas importadas também viram tags, pelo nome da coluna.
        </FieldHint>

        {unknownTags.length > 0 && (
          <Warn>
            Tag não reconhecida: {unknownTags.map((tag) => `{{${tag}}}`).join(', ')}. Se não vier de
            uma coluna importada, ela chega vazia ao destinatário.
          </Warn>
        )}
        {form.body.trim().length > 0 && usedTags.length === 0 && (
          <Warn>
            Este texto não tem nenhuma tag. Mensagem idêntica para todo mundo é o gatilho de spam
            mais reportado no WhatsApp.
          </Warn>
        )}

        {rendered && (
          <>
            <Label>Como o lojista vai receber</Label>
            <Preview>{rendered}</Preview>
          </>
        )}

        <ToggleField>
          <Switch
            checked={form.active}
            onChange={(v) => setForm((prev) => ({ ...prev, active: v }))}
          />
          Template ativo
        </ToggleField>
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Remover template"
        width="420px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Remover
            </Button>
          </>
        }
      >
        <ConfirmText>
          Remover <strong>{deleting?.name}</strong>? Templates usados por alguma campanha não podem
          ser removidos - desative-os em vez disso.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default TemplatesTab;
